import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { cn } from '@/lib/utils'
import { navLinks, studioMeta, CDN } from '@/constants/site'
import { NomaMark } from '@/components/generals/noma-mark'
import { RollText } from '@/components/generals/roll-link'

const menuItems = [{ label: 'Home', href: '/', count: undefined }, ...navLinks]

const socials = [
    {
        label: 'X',
        href: 'https://x.com/',
        path: 'M3 3l7.5 9.6L3.4 21h2L11.4 14l5.1 7h4.6l-7.9-10.1L20.2 3h-2l-5.6 6.5L7.7 3H3Z'
    },
    {
        label: 'Instagram',
        href: 'https://instagram.com/',
        path: 'M12 7.4a4.6 4.6 0 1 0 0 9.2 4.6 4.6 0 0 0 0-9.2Zm0 7.6a3 3 0 1 1 0-6 3 3 0 0 1 0 6ZM17 5.8a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2ZM12 4.1c2.6 0 2.9 0 3.9.06 2.6.12 3.85 1.38 3.97 3.98.05 1 .05 1.3.05 3.86s0 2.86-.05 3.86c-.12 2.6-1.37 3.86-3.97 3.98-1 .05-1.3.05-3.9.05s-2.9 0-3.9-.05c-2.6-.12-3.85-1.38-3.97-3.98C4.07 14.86 4.07 14.56 4.07 12s0-2.86.06-3.86C4.25 5.54 5.5 4.28 8.1 4.16c1-.05 1.3-.06 3.9-.06Zm0-1.7c-2.64 0-2.97.01-4 .06C4.4 2.62 2.62 4.4 2.46 8c-.05 1.03-.06 1.36-.06 4s.01 2.97.06 4c.16 3.6 1.94 5.38 5.54 5.54 1.03.05 1.36.06 4 .06s2.97-.01 4-.06c3.6-.16 5.38-1.94 5.54-5.54.05-1.03.06-1.36.06-4s-.01-2.97-.06-4C21.38 4.4 19.6 2.62 16 2.46c-1.03-.05-1.36-.06-4-.06Z'
    },
    {
        label: 'Dribbble',
        href: 'https://dribbble.com/',
        path: 'M12 2.4a9.6 9.6 0 1 0 0 19.2 9.6 9.6 0 0 0 0-19.2Zm6.34 4.43a8.1 8.1 0 0 1 1.84 5.07c-.27-.06-2.98-.6-5.71-.26-.06-.14-.12-.29-.19-.44-.17-.4-.36-.8-.55-1.19 3.03-1.23 4.41-3.01 4.61-3.18ZM12 3.84c2.05 0 3.92.77 5.34 2.03-.17.24-1.42 1.9-4.34 3-1.35-2.48-2.84-4.51-3.07-4.82A8.2 8.2 0 0 1 12 3.84Zm-3.67.77c.22.3 1.69 2.34 3.05 4.77-3.84 1.02-7.23 1-7.6 1a8.2 8.2 0 0 1 4.55-5.77ZM3.6 12.01v-.25c.35.01 4.34.06 8.44-1.17.24.46.46.93.67 1.4l-.33.1c-4.24 1.37-6.5 5.1-6.68 5.42A8.15 8.15 0 0 1 3.6 12ZM12 20.18a8.14 8.14 0 0 1-5.02-1.72c.15-.3 1.8-3.47 6.44-5.09l.05-.02c1.16 3 1.63 5.51 1.76 6.23A8.15 8.15 0 0 1 12 20.18Zm4.72-1.4c-.08-.52-.52-2.93-1.6-5.88 2.57-.41 4.82.26 5.1.35a8.16 8.16 0 0 1-3.5 5.52Z'
    },
    {
        label: 'Behance',
        href: 'https://behance.net/',
        path: 'M9.1 6.2c.72 0 1.37.06 1.96.19.58.12 1.08.32 1.5.6.4.28.72.65.94 1.11.22.47.33 1.04.33 1.72 0 .73-.17 1.34-.5 1.83-.34.49-.83.89-1.48 1.2.9.26 1.57.71 2.01 1.36.44.64.66 1.42.66 2.33 0 .74-.14 1.37-.43 1.9-.28.54-.67.98-1.15 1.32-.49.34-1.04.59-1.67.75-.62.16-1.26.24-1.92.24H2.4V6.2h6.7Zm-.4 5.53c.59 0 1.08-.14 1.46-.42.38-.28.57-.74.57-1.37 0-.35-.06-.64-.19-.87a1.34 1.34 0 0 0-.51-.52 2.1 2.1 0 0 0-.73-.26 4.9 4.9 0 0 0-.86-.07H5.36v3.51H8.7Zm.18 5.81c.32 0 .63-.03.92-.1.29-.6.55-.16.77-.31.22-.15.4-.35.53-.61.13-.26.2-.6.2-1 0-.79-.22-1.35-.66-1.69-.44-.34-1.03-.5-1.75-.5H5.36v4.21h3.52ZM17.6 17.4c.4.39.97.58 1.72.58.54 0 1-.13 1.38-.4.39-.28.63-.57.72-.87h2.1c-.34 1.04-.85 1.79-1.54 2.24-.69.45-1.53.67-2.51.67-.68 0-1.3-.11-1.85-.33a3.9 3.9 0 0 1-1.4-.94 4.2 4.2 0 0 1-.89-1.46 5.4 5.4 0 0 1-.31-1.87c0-.66.11-1.27.32-1.84.21-.57.51-1.06.9-1.47.39-.41.86-.74 1.4-.98a4.6 4.6 0 0 1 1.83-.35c.75 0 1.4.14 1.96.43.56.29 1.02.68 1.38 1.17.36.49.62 1.05.78 1.68.16.63.21 1.29.16 1.98h-6.32c0 .76.22 1.37.62 1.76Zm3-4.78c-.32-.35-.85-.54-1.51-.54-.44 0-.8.07-1.09.22-.29.15-.52.33-.69.55-.17.22-.29.45-.36.69-.07.24-.11.46-.12.65h3.92c-.12-.61-.32-1.06-.65-1.41v-.16ZM16.4 7.9h4.9v1.2h-4.9V7.9Z'
    }
]

interface MenuOverlayProps {
    open: boolean
    onClose: () => void
}

/* A left drawer, not a full-screen takeover. It slides out from behind the
   fixed rail exactly like the reference: dark panel, oversized nav list, a
   contact pill at the foot, socials and the copyright below it. */
export const MenuOverlay: FC<MenuOverlayProps> = ({ open, onClose }): ReactNode => {
    const scope = useRef<HTMLDivElement>(null)
    const timeline = useRef<gsap.core.Timeline | null>(null)
    const opened = useRef(false)
    const router = useRouter()

    /* The whole panel is hidden at the root, so nothing inside it has to be
       pre-hidden in the markup. That distinction is the fix: the reveal used to
       run off one long-lived paused timeline that was played forward and
       reversed, and every element's VISIBLE state was the end frame of a tween.
       Any interruption to that timeline left the nav words parked below their
       masks with nothing to bring them back, which is the empty drawer. Now each
       open and close builds its own timeline and every tween hands the element
       back to plain CSS when it lands, so the resting state is "visible" rather
       than "whatever the last tween wrote". */
    useGsap(() => {
        const root = scope.current

        if (!root) {
            return
        }

        gsap.set(root, { autoAlpha: 0, pointerEvents: 'none' })

        return () => {
            timeline.current?.kill()
            timeline.current = null
        }
    }, scope, [])

    useEffect(() => {
        const root = scope.current

        if (!root || (!open && !opened.current)) {
            return
        }

        opened.current = true

        const scrim = root.querySelector('[data-menu-scrim]')
        const panel = root.querySelector('[data-menu-panel]')
        const chars = gsap.utils.toArray<HTMLElement>(root.querySelectorAll('[data-menu-char]'))
        const rules = gsap.utils.toArray<HTMLElement>(root.querySelectorAll('[data-menu-rule]'))
        const fades = gsap.utils.toArray<HTMLElement>(root.querySelectorAll('[data-menu-fade]'))
        const inner = [...chars, ...rules, ...fades]

        timeline.current?.kill()
        timeline.current = null

        if (prefersReducedMotion()) {
            gsap.set(inner, { clearProps: 'transform,opacity' })
            gsap.set(root, {
                autoAlpha: open ? 1 : 0,
                pointerEvents: open ? 'auto' : 'none'
            })

            return
        }

        if (open) {
            const tl = gsap.timeline({ defaults: { ease: 'noma' } })

            tl.set(root, { autoAlpha: 1, pointerEvents: 'auto' })

            tl.fromTo(scrim,
                { opacity: 0 },
                { opacity: 1, duration: 0.7 },
                0
            )

            tl.fromTo(panel,
                { xPercent: -102 },
                { xPercent: 0, duration: 0.85 },
                0
            )

            tl.fromTo(chars,
                { yPercent: 145 },
                { yPercent: 0, duration: 0.85, stagger: 0.013, clearProps: 'transform' },
                0.28
            )

            tl.fromTo(rules,
                { scaleX: 0, transformOrigin: 'left' },
                { scaleX: 1, duration: 0.8, stagger: 0.04, clearProps: 'transform' },
                0.3
            )

            tl.fromTo(fades,
                { opacity: 0, y: 20 },
                { opacity: 1, y: 0, duration: 0.7, stagger: 0.05, clearProps: 'opacity,transform' },
                0.5
            )

            timeline.current = tl

            return
        }

        const tl = gsap.timeline({ defaults: { ease: 'noma-soft' } })

        tl.to(fades, { opacity: 0, duration: 0.25 }, 0)
        tl.to(panel, { xPercent: -102, duration: 0.5 }, 0.05)
        tl.to(scrim, { opacity: 0, duration: 0.45 }, 0.05)
        tl.set(root, { autoAlpha: 0, pointerEvents: 'none' })
        tl.set(inner, { clearProps: 'transform,opacity' })

        timeline.current = tl
    }, [open])

    useEffect(() => {
        if (!open) {
            return
        }

        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                onClose()
            }
        }

        window.addEventListener('keydown', onKey)

        return () => {
            window.removeEventListener('keydown', onKey)
        }
    }, [open, onClose])

    return (
        <div
            ref={scope}
            className='invisible fixed inset-0 z-[110]'
            role='dialog'
            aria-modal='true'
            aria-label='Site menu'
        >
            <button
                type='button'
                data-menu-scrim
                onClick={onClose}
                tabIndex={open ? 0 : -1}
                aria-label='Close menu'
                className='absolute inset-0 size-full cursor-default bg-ink/70 backdrop-blur-[3px]'
            />

            <div
                data-menu-panel
                className='absolute inset-y-0 left-0 flex w-[min(92vw,437px)] flex-col justify-between overflow-y-auto border-r border-paper/15 bg-ink-soft px-7 py-8 lg:px-8 lg:py-[30px]'
            >
                <div className='flex flex-col gap-10 lg:gap-14'>
                    <div className='flex items-center justify-between'>
                        <Link
                            href='/'
                            onClick={onClose}
                            data-cursor='link'
                            className='group/roll flex items-center gap-2 text-paper'
                            aria-label='Strength Scaling, home'
                        >
                            <NomaMark className='size-[24px] transition-colors duration-500 group-hover/roll:text-brand' />

                            <RollText label='Strength Scaling' className='t-display pb-[0.06em] text-[19px] leading-none' />
                        </Link>

                        <button
                            type='button'
                            onClick={onClose}
                            data-cursor='link'
                            aria-label='Close menu'
                            className='flex size-9 items-center justify-center rounded-full border border-paper/20 text-paper transition-colors duration-300 hover:border-brand hover:bg-brand lg:hidden'
                        >
                            <svg viewBox='0 0 24 24' className='size-4' aria-hidden='true'>
                                <path d='M5 5l14 14M19 5L5 19' stroke='currentColor' strokeWidth='1.8' />
                            </svg>
                        </button>
                    </div>

                    <nav className='flex flex-col gap-[7px]' aria-label='Menu'>
                        {menuItems.map((item) => {
                            const active = router.pathname === item.href
                                || (item.href !== '/' && router.pathname.startsWith(`${item.href}/`))

                            return (
                                <Link
                                    key={item.href}
                                    href={item.href}
                                    onClick={onClose}
                                    data-cursor='link'
                                    aria-current={active ? 'page' : undefined}
                                    className='group/menu flex items-start gap-1.5'
                                >
                                    <span className='split-word overflow-hidden py-[0.16em] -my-[0.16em]'>
                                        {Array.from(item.label).map((char, charIndex) => (
                                            <span
                                                key={`${char}-${charIndex}`}
                                                data-menu-char
                                                className={cn(
                                                    't-cond split-char text-[clamp(30px,4.2vw,40px)] leading-[1.1] transition-colors duration-500',
                                                    active ? 'text-brand' : 'text-paper group-hover/menu:text-brand'
                                                )}
                                            >
                                                {char}
                                            </span>
                                        ))}
                                    </span>

                                    {item.count
                                        ? (
                                            <span data-menu-fade className='t-mono mt-[0.5em] text-[11px] text-brand'>
                                                {item.count}
                                            </span>
                                        )
                                        : null}
                                </Link>
                            )
                        })}
                    </nav>

                    <div data-menu-fade className='flex flex-col gap-5 border-y border-paper/10 py-6'>
                        <div className='flex items-center gap-2'>
                            <span className='relative flex size-2'>
                                <span className='absolute inset-0 animate-ping rounded-full bg-brand opacity-60' />

                                <span className='relative block size-2 rounded-full bg-brand' />
                            </span>

                            <span className='t-mono text-ash-soft'>
                                2 slots open &middot; {studioMeta.established}
                            </span>
                        </div>

                        <div className='grid grid-cols-2 gap-4'>
                            <div className='flex flex-col gap-1'>
                                <span className='t-mono text-[10px] text-ash-soft/70'>
                                    Email
                                </span>

                                <a
                                    href={`mailto:${studioMeta.email}`}
                                    data-cursor='link'
                                    className='t-cond-soft text-[14px] text-paper transition-colors duration-300 hover:text-brand'
                                >
                                    {studioMeta.email}
                                </a>
                            </div>

                            <div className='flex flex-col gap-1'>
                                <span className='t-mono text-[10px] text-ash-soft/70'>
                                    Phone
                                </span>

                                <a
                                    href={`tel:${studioMeta.phoneTel}`}
                                    data-cursor='link'
                                    className='t-cond-soft text-[14px] text-paper transition-colors duration-300 hover:text-brand'
                                >
                                    {studioMeta.phone}
                                </a>
                            </div>

                            <div className='flex flex-col gap-1'>
                                <span className='t-mono text-[10px] text-ash-soft/70'>
                                    Based in
                                </span>

                                <span className='t-cond-soft text-[14px] capitalize text-paper'>
                                    {studioMeta.bases.join(', ')}
                                </span>
                            </div>

                            <div className='flex flex-col gap-1'>
                                <span className='t-mono text-[10px] text-ash-soft/70'>
                                    Services
                                </span>

                                <span className='t-cond-soft text-[14px] text-paper'>
                                    {studioMeta.services.length} disciplines
                                </span>
                            </div>
                        </div>
                    </div>

                    <div data-menu-fade className='flex flex-col gap-4'>
                        <span className='t-cond text-[15px] text-ash-soft'>
                            let&apos;s talk
                        </span>

                        <Link
                            href='/contact'
                            onClick={onClose}
                            data-cursor='link'
                            className='group/pill flex items-center justify-between gap-3 rounded-full bg-brand p-1.5 pr-2 text-paper'
                        >
                            <span className='flex items-center gap-3'>
                                <img
                                    src={`${CDN}/Jfjs0nvrWkQ5vWOcalwIPI1ovEU.jpeg`}
                                    alt=''
                                    className='size-8 rounded-full object-cover'
                                    loading='lazy'
                                />

                                <span className='t-cond text-[15px]'>
                                    Start a project
                                </span>
                            </span>

                            <span className='flex size-7 items-center justify-center rounded-full bg-paper/20 transition-transform duration-500 group-hover/pill:rotate-45'>
                                <svg viewBox='0 0 24 24' className='size-3.5' fill='none' aria-hidden='true'>
                                    <path d='M5 19L19 5M19 5H8M19 5v11' stroke='currentColor' strokeWidth='2' />
                                </svg>
                            </span>
                        </Link>
                    </div>
                </div>

                <div className='flex flex-col gap-5 pt-10'>
                    <span data-menu-rule className='block h-px origin-left bg-paper/15' />

                    <div data-menu-fade className='flex items-center gap-4'>
                        {socials.map((social) => (
                            <a
                                key={social.label}
                                href={social.href}
                                target='_blank'
                                rel='noreferrer'
                                data-cursor='link'
                                aria-label={social.label}
                                className='text-ash-soft transition-colors duration-300 hover:text-brand'
                            >
                                <svg viewBox='0 0 24 24' className='size-[18px]' aria-hidden='true'>
                                    <path d={social.path} fill='currentColor' />
                                </svg>
                            </a>
                        ))}
                    </div>

                    <span data-menu-fade className='t-cond-soft block text-[12px] leading-[1.3] text-ash-soft'>
                        <span className='block'>
                            2026 Noma
                            {studioMeta.mark
                                ? (
                                    <>
                                        {' '}
                                        <span className='text-[1.2em] leading-none'>
                                            {studioMeta.mark}
                                        </span>
                                    </>
                                )
                                : null}
                        </span>

                        <span className='block'>
                            All rights reserved.
                        </span>
                    </span>
                </div>
            </div>
        </div>
    )
}
